import { get, uniq } from 'lodash';
import {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
  NextApiRequest,
  NextApiResponse,
} from 'next';

import { LocaleLanguageMapper } from '../constants';
import {
  UserAgentDevicesEnum,
  Locale,
  UserAgentDeviceType,
  Config,
  AppProps,
  componentsRender,
  Component,
  ComponentImport,
  DefaultProps,
} from '@bolttech/essentials';
import { componentsImport } from '@bolttech/essentials/src/components';

import { urlEncode, urlParse } from '../utils/url.util';

import { parseSession } from '../../modules/session/api/session';
import { getUtmUrl } from '../../modules/session/api/utm';
import { getSession } from '../../modules/session/services/session.service';
import { parseUtms } from '../utils/path-mapper.util';

/*  TODO: urlMap on frontend-shared was ref by @client(country-instance)
let's keep it as a param any and thing a way to change it in the future

TODO: change the type of parameters urlMap, importMapper
*/

async function getProps<O = Record<string, unknown>>(
  url: string,
  pageDevice: UserAgentDevicesEnum,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  urlMap: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  importMapper: any,
  options?: O,
): Promise<Record<string, unknown>> {
  const {
    dev,
    appInstance,
    appTitle,
    cmsRedisHost,
    gatewayUrl,
    bundleUrl,
    localeCountry,
    localeCurrency,
    localeCurrencySymbol,
    cdnUrl,
    googleApiKey,
    claUrl,
  } = await import('../constants');
  const { getLocaleFromUrl } = await import('../services/instance.service');
  const { CmsService } = await import('../../modules/cms-processwire');
  const { getFlowAndPartnerByPath, getPageNameAndVerticalByPath } =
    await import('../utils/path-mapper.util');
  const { filterComponentByDevice, filterComponentByLanguage } = await import(
    '../utils'
  );
  const { getTranslations } = await import(
    '../../modules/translations/utils/get-translations.util'
  );

  const locale = getLocaleFromUrl(url || '/', appInstance);
  const { pageName, vertical } = getPageNameAndVerticalByPath(url, urlMap);
  const flowDetails = getFlowAndPartnerByPath(url, urlMap);
  const { source, flow, partner } =
    (flowDetails as Record<string, string>) || {};
  const isMobile = pageDevice === UserAgentDevicesEnum.Mobile;

  const config = {
    dev,
    appInstance,
    appTitle,
    cmsRedisHost,
    gatewayUrl,
    bundleUrl,
    localeCountry,
    localeCurrency,
    localeCurrencySymbol,
    cdnUrl,
    googleApiKey,
    claUrl,
    source,
    flow,
    partner,
    vertical,
    locale,
    pageName,
    pageDevice,
    isMobile,
    ...(get(options, 'config') || {}),
  };

  const props = {
    config,
    components: [] as Component<DefaultProps, DefaultProps>[],
    translations: {},
  };

  if (cmsRedisHost) {
    const cmsService = new CmsService();
    const { components } = await cmsService.renderComponents({
      url,
      locale,
      vertical,
      pageDevice,
      importMapper: importMapper,
    });

    props.components = components.filter(
      (component) =>
        filterComponentByDevice(component, pageDevice) &&
        filterComponentByLanguage(
          component,
          LocaleLanguageMapper[locale as Locale],
        ),
    );

    props.components
      .filter((component) => component.name === 'CustomHtml')
      .forEach((component) => {
        console.log(component.props);
      });
  }

  if (gatewayUrl) {
    props.translations = await getTranslations({
      pageName,
      vertical,
      locale,
      flow,
      partner,
    });
  }

  return props;
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const { RedisService } = await import('../services/redis');
  const { configService } = await import('../../modules/config');
  const { urlEncode, urlFilter, urlMapper } = await import('../utils/url.util');

  const cmsRedisService = new RedisService(configService);
  const cmsUrlMapping = await cmsRedisService.hgetall('cms_url_mapping');
  const cmsUrlMappingKeys = Object.keys(
    cmsUrlMapping as { [key: string]: string },
  );
  const urls = uniq(cmsUrlMappingKeys.filter(urlFilter).map(urlMapper)).map(
    urlEncode,
  );
  const paths = [
    ...urls.map((url: string) => {
      const slug = url
        .substring(1)
        .split('/')
        .filter((item) => item !== '');
      slug.push('desktop');
      return { params: { slug } };
    }),
    ...urls.map((url: string) => {
      const slug = url
        .substring(1)
        .split('/')
        .filter((item) => item !== '');
      slug.push('mobile');
      return { params: { slug } };
    }),
  ];

  return { paths, fallback: false };
}

export async function getStaticProps<O = Record<string, unknown>>(
  { params }: GetStaticPropsContext,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  urlMap: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  importMapper: any,
  options?: O,
): Promise<GetStaticPropsResult<Record<string, unknown>>> {
  const slug = (params?.slug as string[]) || [''];
  const pageDevice =
    slug[slug.length - 1] === 'mobile'
      ? UserAgentDevicesEnum.Mobile
      : UserAgentDevicesEnum.Desktop;
  slug.pop();

  const url = slug.join('/').replace(/^/, '/');
  const props = await getProps(url, pageDevice, urlMap, importMapper, options);

  return {
    props: JSON.parse(JSON.stringify(props)),
  };
}

export async function getServerSideProps<O = Record<string, unknown>>(
  { req, res, query }: GetServerSidePropsContext,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  urlMap: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  importMapper: any,
  options?: O,
): Promise<GetServerSidePropsResult<Record<string, unknown>>> {
  const userAgent = req?.headers['user-agent'] as string;
  const isMobile =
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
      userAgent,
    ) ||
    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
      userAgent.substring(0, 4),
    );
  const pageDevice: UserAgentDeviceType = isMobile
    ? UserAgentDevicesEnum.Mobile
    : UserAgentDevicesEnum.Desktop;
  const urlWithoutParams = req?.url?.split('?')?.[0] || req?.url;
  const initialProps = await getProps(
    urlWithoutParams || '/',
    pageDevice,
    urlMap,
    importMapper,
    options,
  );
  const config = initialProps.config as Config;

  const session = await parseSession(
    req as NextApiRequest,
    await getSession(req, res),
    config.locale,
    config.vertical,
    query as Record<string, string>,
  );

  const parsedUrl = urlParse(
    decodeURIComponent(req?.url as string).replace(
      /\/_next\/data\/development(.+)\.json.+/,
      '$1',
    ),
  );
  const { pathname } = parsedUrl;
  const url = decodeURIComponent(pathname as string);

  const utmUrl = await getUtmUrl(
    req as NextApiRequest,
    res as NextApiResponse,
    session,
    config,
    url,
    options as { partnersWithoutHomepage: string[] } | undefined,
    query,
  );

  let utms = get(session, 'utm'); // It's for apps that using frontendV1 and frontendV2
  if (!utms) {
    utms = get(session, 'utms'); // It's for apps that only using frontendV2
  }
  const parameters = config.vertical && parseUtms(utms[config.vertical]);

  if (url !== utmUrl) {
    const encodedUrl = urlEncode(utmUrl);
    const destination = `${encodedUrl}${
      utmUrl.match(/\?/) && parameters ? `&` : `?`
    }${parameters}`;
    return {
      redirect: {
        permanent: false,
        destination,
      },
    };
  }

  const props = {
    ...(await getProps(url || '/', pageDevice, urlMap, importMapper, options)),
    session,
  };

  return {
    props: JSON.parse(JSON.stringify(props)),
  };
}

export const App =
  (componentList: ComponentImport): ((props: AppProps) => JSX.Element) =>
  // eslint-disable-next-line react/display-name
  (props: AppProps): JSX.Element => {
    const components =
      (props.components && componentsImport(componentList, props.components)) ||
      [];

    return <>{componentsRender(components)}</>;
  };
